import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { msalInstance, clearStaleInteraction } from '@/auth/msal';
import { getLoginHint, getAuth0LoginHint } from '@/auth/login-hint';
import { isAuth0Authenticated, loginToAuth0 } from '@/auth/auth0';
import { getShellAuthProvider, resolveClientIdStrict } from '@/auth/auth-provider';
import { isInteractionRequired, clearInteractionRequired } from '@/auth/interaction-required';
import { markLoginTime } from '@/auth/session-duration';
import { LegacySpinner } from '@/ui/LegacySpinner';
import { ShellFooter } from '@/ui/ShellFooter';
import { MotifButton } from '@ey-xd/motif-wc-react';

const REFRAME_LINK = 'https://www.ey.com/en_us/megatrends/how-megatrends-can-reframe-your-future';

export function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Legacy `login.component` parity: the sign-in button is shown ONLY when an
  // interaction is required (post-logout / idle timeout / access-denied — the
  // `ey.interaction.needed` flag). On a normal fresh-session entry the page
  // auto-initiates login and shows a spinner, so the user never sees — or has
  // to click — a login button. Seeding from the flag keeps the first paint
  // correct (spinner vs button) with no flash.
  const [displayLogin, setDisplayLogin] = useState<boolean>(() => isInteractionRequired());
  // Prevents a double `loginRedirect` (`interaction_in_progress`) from the
  // auto-login effect and any subsequent re-render / click.
  const startedRef = useRef(false);

  const beginLogin = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setError(null);
    // Clear any leftover MSAL redirect state + the interaction flag before we
    // start (legacy `login.service.logIn` removes `msal.interaction.status`;
    // `login.component.internalSignIn` clears the interaction-required flag).
    clearStaleInteraction();
    clearInteractionRequired();
    // Anchor the absolute session-duration cap to this genuine login (legacy
    // `login.component` writes `login.time` on login initiation).
    markLoginTime();
    // The redirect never left — re-show the button so the user can retry.
    const onError = (err: unknown) => {
      startedRef.current = false;
      setDisplayLogin(true);
      setError(err instanceof Error ? err.message : String(err));
    };
    // Independent Auth0 shell: sign in through Auth0 with no MSAL involvement.
    if (getShellAuthProvider() === 'auth0') {
      void loginToAuth0({
        paramclientid: resolveClientIdStrict() ?? undefined,
        returnTo: '/',
        loginHint: getAuth0LoginHint(),
      }).catch(onError);
      return;
    }
    const loginHint = getLoginHint();
    msalInstance.loginRedirect({ scopes: [], ...(loginHint ? { loginHint } : {}) }).catch(onError);
  };

  useEffect(() => {
    // Interaction required (logout / idle / access-denied) -> show the button
    // and wait for an explicit click; never auto-redirect (legacy
    // `displayLogin`).
    if (isInteractionRequired()) {
      setDisplayLogin(true);
      return;
    }
    // Independent Auth0 shell: there is no MSAL account to check — forward an
    // already-signed-in user home based on the Auth0 session, else auto-initiate
    // the Auth0 login (same spinner-first UX as the MSAL path below).
    if (getShellAuthProvider() === 'auth0') {
      void isAuth0Authenticated().then((authed) => {
        if (authed) void navigate({ to: '/', replace: true });
        else beginLogin();
      });
      return;
    }
    // A live session already exists (navigated to /login with an account) ->
    // forward into the shell instead of re-authenticating.
    if (msalInstance.getActiveAccount()) {
      void navigate({ to: '/', replace: true });
      return;
    }
    // No account + no interaction flag -> auto-initiate login and show the
    // spinner, matching legacy `login.component.ngOnInit`. `beginLogin` is
    // idempotent via `startedRef`, so running once on mount is sufficient.
    beginLogin();
  }, [navigate]);

  const signIn = () => {
    // Explicit click after an interaction-required stop or an error retry.
    startedRef.current = false;
    beginLogin();
  };

  // Auto-login in progress — legacy shows the EY three-bar spinner with the
  // "Loading your session" caption instead of the login form (mirrors
  // `FrameRedirectPage`).
  if (!displayLogin) {
    return (
      <section
        className="login-page__loading"
        aria-live="polite"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: '1rem',
          padding: '2rem',
        }}
      >
        <LegacySpinner />
        <div>
          Loading your session
          <br />
          You will be redirected in a few seconds....
        </div>
      </section>
    );
  }

  return (
    <section className="login-page" aria-labelledby="login-page-title">
      <div className="login-page__form">
        <div className="login-page__branding">
          <img
            className="login-page__logo"
            src="/ey-logo-black.svg"
            alt="EY"
            height="61"
            width="57"
          />
          <span className="login-page__divider" />
          <span className="login-page__title">Global Tax Platform</span>
        </div>

        <div className="login-page__actions">
          {error ? <p className="login-page__error">{error}</p> : null}
          <MotifButton
            variant="primary"
            type="button"
            role="button"
            aria-label="Login"
            fullWidth
            className="login-page__btn"
            onClick={signIn}
          >
            Login
          </MotifButton>
        </div>
      </div>

      <div
        className="login-page__hero"
        style={{ backgroundImage: "url('/ey-reframe-your-future-lighthouse.jpg')" }}
      >
        <div className="login-page__hero-content">
          <span className="login-page__hero-title">
            Are you reframing your future or is the future reframing you?
          </span>
          <a className="login-page__read-more" href={REFRAME_LINK} target="_blank" rel="noopener">
            Read more
          </a>
        </div>
      </div>

      <ShellFooter />
    </section>
  );
}
